import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    FiArrowRight,
    FiCheckCircle,
    FiChevronDown,
    FiClock,
    FiEye,
    FiPlus,
    FiRefreshCw,
    FiShield,
    FiSmartphone,
    FiXCircle,
} from "react-icons/fi";
import { MyContext } from "../../Context/MyProvider";
import { showTost } from "../../showTost";
import ThemedPageFrame from "./ThemedPageFrame";
import {
    getXsrfTokenCookie,
    SHOP_API_TOKEN_STORAGE_KEY,
    SHOP_RECHARGE_ENDPOINTS,
    isUserLoggedIn,
    requireLogin,
} from "../../utils/auth";
import {
    openRechargeRazorpay,
    saveRechargePaymentStatus,
} from "../../utils/rechargePayment";
import "../../Style/style.css";

const AMOUNT_PRESETS = [10, 199, 299, 349, 499, 599, 999];

const getList = (data, key) => {
    if (Array.isArray(data?.[key])) return data[key];
    if (Array.isArray(data?.data?.[key])) return data.data[key];
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const getErrorMessage = (error) => {
    const responseMessage = error?.response?.data?.msg || error?.response?.data?.message;

    if (responseMessage) {
        try {
            const parsed =
                typeof responseMessage === "string"
                    ? JSON.parse(responseMessage)
                    : responseMessage;

            return parsed?.msg || parsed?.message || responseMessage;
        } catch {
            return responseMessage;
        }
    }

    return error?.message || "Unable to complete the request.";
};

const getAuthConfig = () => {
    const token =
        getXsrfTokenCookie() ||
        localStorage.getItem(SHOP_API_TOKEN_STORAGE_KEY);

    return {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    };
};

const maskMobile = (mobile = "") =>
    mobile.length > 4
        ? `${mobile.slice(0, 4)} •••• ${mobile.slice(-3)}`
        : mobile;

const formatDate = (value) => {
    if (!value) return "Date unavailable";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};


const getOperatorInitial = (name = "") =>
    name === "BSNL" || name === "MTNL"
        ? name.slice(0, 2)
        : name.slice(0, 1).toUpperCase();

const getStatus = (value) => String(value || "pending").toLowerCase();

const getStatusMeta = (status) => {
    if (["success", "successful", "completed"].includes(status)) {
        return {
            label: "Successful",
            className: "success",
            icon: <FiCheckCircle />,
        };
    }

    if (status === "failed") {
        return {
            label: "Failed",
            className: "failed",
            icon: <FiXCircle />,
        };
    }

    return {
        label: "Pending",
        className: "pending",
        icon: <FiClock />,
    };
};

const isSuccessfulResponse = (data = {}) => {
    if (data?.status === false || data?.success === false) return false;
    if (String(data?.ResponseCode || data?.response_code || "") === "400") return false;

    return true;
};

const getHistoryText = (item = {}) =>
    [
        item?.operator_type,
        item?.service_name,
        item?.operator_name,
        item?.operator,
        item?.recharge_type,
        item?.type,
        item?.category_name,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

const looksLikeMobileHistory = (item = {}) => {
    const searchable = getHistoryText(item);
    const mobileNo = String(item?.mobile_no || item?.mobile || "").replace(/\D/g, "");

    if (
        searchable.includes("dth") ||
        searchable.includes("direct to home") ||
        searchable.includes("bill") ||
        item?.customer_email ||
        item?.customer_number ||
        item?.customer_no ||
        item?.customer_id ||
        item?.subscriber_id ||
        item?.p1
    ) {
        return false;
    }

    return /^[6-9]\d{9}$/.test(mobileNo);
};

const MobileRecharge = () => {
    const { setIsLoginShow, isLogin, BaseUrl } = useContext(MyContext);
    const navigate = useNavigate();
    const location = useLocation();

    const serviceId = location.state?.serviceId || new URLSearchParams(location.search).get("serviceId");

    const [operators, setOperators] = useState([]);
    const [circles, setCircles] = useState([]);
    const [history, setHistory] = useState([]);
    const [plans, setPlans] = useState([]);

    const [mobileNumber, setMobileNumber] = useState("");
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [selectedCircle, setSelectedCircle] = useState(null);
    const [amount, setAmount] = useState("");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const [showAllHistory, setShowAllHistory] = useState(false);
    const [showRechargeForm, setShowRechargeForm] = useState(false);
    const [paymentResponse, setPaymentResponse] = useState(null);

    const loadHistory = async () => {
        const response = await axios.get(
            SHOP_RECHARGE_ENDPOINTS.history,
            getAuthConfig()
        );

        setHistory(getList(response.data, "data").filter(looksLikeMobileHistory));
    };

    useEffect(() => {
        if (!isUserLoggedIn()) {
            setLoading(false);
            return;
        }

        const loadRechargeData = async () => {
            try {
                setError("");

                const config = getAuthConfig();
                const [
                    operatorResponse,
                    circleResponse,
                    historyResponse,
                    plansResponse,
                ] = await Promise.all([
                    axios.get(
                        `https://shop.nabhiverse.com/api/v1/operator/list/${serviceId}`,
                        config
                    ),
                    axios.get(SHOP_RECHARGE_ENDPOINTS.circles, config),
                    axios.get(SHOP_RECHARGE_ENDPOINTS.history, config),
                    axios.get(
                        "https://shop.nabhiverse.com/api/v1/plans",
                        config
                    ),
                ]);
                const availableOperators = operatorResponse?.data?.response || [];
                const allPlans =
                    plansResponse?.data?.response ||
                    plansResponse?.data?.data ||
                    [];

                const availablePlans = allPlans.filter(
                    (plan) => String(plan?.service_id) === String(serviceId)
                );

                const availableCircles = getList(
                    circleResponse.data,
                    "circles"
                );

                setOperators(availableOperators);
                setCircles(availableCircles);
                setPlans(availablePlans);
                setHistory(getList(historyResponse.data, "data").filter(looksLikeMobileHistory));

                setSelectedOperator(null);
                setSelectedCircle(null);
            } catch (requestError) {
                setError(getErrorMessage(requestError));
            } finally {
                setLoading(false);
            }
        };

        loadRechargeData();
    }, [isLogin, serviceId]);

    const amountLimits = useMemo(
        () => ({
            min: Number(
                selectedOperator?.operator_min_recharge_amount || 10
            ),
            max: Number(
                selectedOperator?.operator_max_recharge_amount || 100000
            ),
        }),
        [selectedOperator]
    );

    const operatorMap = useMemo(() => {
        return operators.reduce((map, operator) => {
            const id = operator?.id;
            const operatorId = operator?.operator_id;
            const code = operator?.operator_code;

            if (id !== undefined && id !== null) {
                map[`id:${id}`] = operator;
            }

            if (operatorId !== undefined && operatorId !== null) {
                map[`operator_id:${operatorId}`] = operator;
            }

            if (code !== undefined && code !== null) {
                map[`code:${code}`] = operator;
            }

            return map;
        }, {});
    }, [operators]);

    const circleMap = useMemo(() => {
        return circles.reduce((map, circle) => {
            if (circle?.circle_code !== undefined) {
                map[String(circle.circle_code)] = circle;
            }

            return map;
        }, {});
    }, [circles]);

    const visibleHistory = showAllHistory
        ? history
        : history.slice(0, 5);

    const getHistoryOperator = (item) =>
        operatorMap[`operator_id:${item?.operator_id}`] ||
        operatorMap[`id:${item?.operator_id}`] ||
        operatorMap[`code:${item?.operator_code}`];

    const getHistoryCircle = (item) =>
        circleMap[String(item?.circle_code)];

    const validateForm = () => {
        if (!/^[6-9]\d{9}$/.test(mobileNumber)) {
            return "Enter a valid 10-digit mobile number.";
        }

        if (!selectedOperator) {
            return "Select a mobile operator.";
        }

        if (!selectedCircle) {
            return "Select a circle.";
        }

        if (
            !amount ||
            Number(amount) < amountLimits.min ||
            Number(amount) > amountLimits.max
        ) {
            return `Enter an amount between ₹${amountLimits.min} and ₹${amountLimits.max}.`;
        }

        return "";
    };

    const buildRechargePayload = () => ({
        service_type: "prepaid",
        mobile_no: mobileNumber,
        operator_code: selectedOperator.operator_code,
        amount: Number(amount),
        partner_request_id: `NABHI-${Date.now()}`,
        circle: Number(selectedCircle.circle_code),
        user_var1: "",
        user_var2: "",
        user_var3: "",
    });

    const handleRecharge = async () => {
        if (!requireLogin(setIsLoginShow)) return;

        const validationMessage = validateForm();

        if (validationMessage) {
            showTost(validationMessage, "error");
            return;
        }

        setSubmitting(true);
        setPaymentResponse(null);

        try {
            const payload = buildRechargePayload();
            const payment = await openRechargeRazorpay({
                baseUrl: BaseUrl,
                amount: payload.amount,
                description: `Mobile recharge for ${mobileNumber}`,
                serviceLabel: "Mobile Recharge",
            });

            const response = await axios.post(
                SHOP_RECHARGE_ENDPOINTS.order,
                {
                    ...payload,
                    razorpay_payment_id: payment?.razorpay_payment_id,
                    razorpay_order_id: payment?.razorpay_order_id,
                    razorpay_signature: payment?.razorpay_signature,
                    payment_status: "success",
                },
                getAuthConfig()
            );

            const data = response?.data || {};

            if (!isSuccessfulResponse(data)) {
                throw new Error(data?.message || data?.msg || "Recharge request failed.");
            }

            setPaymentResponse({
                status: true,
                message: data?.message || data?.ResponseMsg || "Recharge request submitted successfully.",
                request_id: data?.request_id || payload.partner_request_id,
                transaction: data?.transaction || {
                    status: data?.status || "submitted",
                },
            });

            saveRechargePaymentStatus({
                status: "success",
                serviceLabel: "Mobile Recharge",
                amount: payload.amount,
                message: data?.message || data?.ResponseMsg || "Recharge request submitted successfully.",
                requestId: data?.request_id || payload.partner_request_id,
                razorpayOrderId: payment?.razorpay_order_id,
                razorpayPaymentId: payment?.razorpay_payment_id,
                retryPath: "/mobile-recharge",
            });

            showTost(
                data?.message || data?.ResponseMsg || "Recharge request submitted successfully.",
                "success"
            );

            setAmount("");
            await loadHistory();
            setShowRechargeForm(false);
            navigate("/recharge-payment-status?status=success");
        } catch (requestError) {
            const message = getErrorMessage(requestError);

            saveRechargePaymentStatus({
                status: "failed",
                serviceLabel: "Mobile Recharge",
                amount,
                message,
                retryPath: "/mobile-recharge",
            });

            showTost(message, "error");
            await loadHistory().catch(() => { });
            navigate("/recharge-payment-status?status=failed");
        } finally {
            setSubmitting(false);
        }
    };

    const openNewRecharge = () => {
        if (!requireLogin(setIsLoginShow, "/mobile-recharge")) return;

        setMobileNumber("");
        setSelectedOperator(null);
        setSelectedCircle(null);
        setAmount("");
        setPaymentResponse(null);
        setError("");
        setShowRechargeForm(true);

        window.setTimeout(() => {
            document
                .getElementById("mobile-recharge-form")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
    };

    const handleRechargeAgain = (item) => {
        if (!requireLogin(setIsLoginShow, "/mobile-recharge")) return;

        const operator = getHistoryOperator(item);
        const circle = getHistoryCircle(item);

        setMobileNumber(String(item?.mobile_no || ""));
        setSelectedOperator(operator || null);
        setSelectedCircle(circle || null);
        setAmount(String(item?.amount || ""));
        // setOperatorSearch("");
        setPaymentResponse(null);
        setError("");
        setShowRechargeForm(true);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };
    const getOperatorPlans = (operator) => {
        if (
            !operator?.operator_id ||
            !Array.isArray(operator?.operator_recharge_plan) ||
            operator.operator_recharge_plan.length === 0
        ) {
            return [];
        }

        return plans.filter(
            (plan) =>
                String(plan?.service_id) === String(serviceId) &&
                String(plan?.operator_id) ===
                String(operator.operator_id)
        );
    };
    const selectedOperatorPlans = useMemo(
        () => getOperatorPlans(selectedOperator),
        [selectedOperator, plans, serviceId]
    );
    const handleOperatorSelect = (operator) => {
        setSelectedOperator(operator);


        setSelectedCircle(null);
    };

    return (
        <ThemedPageFrame
            activePath="/mobile-recharge"
            eyebrow="NabhiVerse Payments"
            title="Mobile Recharge"
            subtitle="Recharge instantly and securely from one place."
            className="mobile_recharge_page"
        >
            <div className="mobile_recharge_shell">
                {error && (
                    <div className="mobile_recharge_alert">
                        <FiXCircle />
                        <span>{error}</span>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                        >
                            <FiRefreshCw />
                            Retry
                        </button>
                    </div>
                )}

                {/* History comes first, like a real recharge app. */}
                <section className="mobile_recharge_history_section">
                    <div className="mobile_recharge_section_header">
                        <div>
                            <span className="mobile_recharge_section_kicker">
                                Recharge activity
                            </span>
                            <h2>Recent Recharges</h2>
                            <p>
                                Quickly recharge a number you have used before.
                            </p>
                        </div>

                        <div className="mobile_recharge_history_actions">
                            {history.length > 5 && (
                                <button
                                    type="button"
                                    className="mobile_recharge_text_button"
                                    onClick={() =>
                                        setShowAllHistory((current) => !current)
                                    }
                                >
                                    <FiEye />
                                    {showAllHistory
                                        ? "Show less"
                                        : "View all"}
                                </button>
                            )}

                            <button
                                type="button"
                                className="mobile_recharge_new_button"
                                onClick={openNewRecharge}
                            >
                                <FiPlus />
                                New Recharge
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="mobile_recharge_history_grid">
                            {[1, 2, 3].map((item) => (
                                <div
                                    className="mobile_recharge_skeleton_card"
                                    key={item}
                                >
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            ))}
                        </div>
                    ) : visibleHistory.length === 0 ? (
                        <div className="mobile_recharge_empty">
                            <div className="mobile_recharge_empty_icon">
                                <FiSmartphone />
                            </div>
                            <strong>No recent recharges</strong>
                            <span>
                                Your recharge activity will appear here after
                                your first transaction.
                            </span>
                            <button
                                type="button"
                                onClick={openNewRecharge}
                            >
                                <FiPlus />
                                Start a Recharge
                            </button>
                        </div>
                    ) : (
                        <div className="mobile_recharge_history_grid">
                            {visibleHistory.map((item, index) => {
                                const status = getStatus(item?.status);
                                const statusMeta = getStatusMeta(status);
                                const operator =
                                    getHistoryOperator(item);
                                const circle = getHistoryCircle(item);

                                return (
                                    <article
                                        className="mobile_recharge_history_card"
                                        key={item?.id || index}
                                        onClick={() =>
                                            handleRechargeAgain(item)
                                        }
                                    >
                                        <div className="mobile_recharge_history_card_top">
                                            <div
                                                className={`mobile_recharge_operator_logo operator-${String(
                                                    operator?.displayName ||
                                                    "mobile"
                                                )
                                                    .toLowerCase()
                                                    .replace(/\s+/g, "-")}`}
                                            >
                                                {getOperatorInitial(
                                                    operator?.displayName ||
                                                    "Mobile"
                                                )}
                                            </div>

                                            <div className="mobile_recharge_history_identity">
                                                <strong>
                                                    {operator?.displayName ||
                                                        "Mobile Recharge"}
                                                </strong>
                                                <span>
                                                    {maskMobile(
                                                        String(
                                                            item?.mobile_no ||
                                                            ""
                                                        )
                                                    )}
                                                </span>
                                            </div>

                                            <div className="mobile_recharge_history_price">
                                                <strong>
                                                    ₹{item?.amount || "0"}
                                                </strong>
                                                <span>
                                                    {formatDate(
                                                        item?.created_at
                                                    )}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mobile_recharge_history_card_bottom">
                                            <span className="mobile_recharge_circle_tag">
                                                {circle?.circle_name ||
                                                    item?.circle_code ||
                                                    "Circle unavailable"}
                                            </span>

                                            <span
                                                className={`mobile_recharge_status ${statusMeta.className}`}
                                            >
                                                {statusMeta.icon}
                                                {statusMeta.label}
                                            </span>

                                            <span className="mobile_recharge_recharge_again">
                                                Recharge again
                                                <FiArrowRight />
                                            </span>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Recharge form */}
                <section
                    id="mobile-recharge-form"
                    className={`mobile_recharge_form_section ${showRechargeForm
                        ? "is-visible"
                        : "is-hidden-mobile"
                        }`}
                >
                    <div className="mobile_recharge_form_header">
                        <div>
                            <span className="mobile_recharge_section_kicker">
                                Prepaid
                            </span>
                            <h2>Recharge mobile</h2>
                            <p>Enter your number and follow the steps to recharge.</p>
                        </div>

                        <div className="mobile_recharge_form_header_actions">
                            <div className="mobile_recharge_secure_badge">
                                <FiShield />
                                Secure payment
                            </div>

                        </div>
                    </div>

                    <div className="mobile_recharge_form_card">
                        <div className="mobile_recharge_field">
                            <label
                                className="mobile_recharge_label"
                                htmlFor="recharge-mobile"
                            >
                                Mobile number
                            </label>

                            <div className="mobile_recharge_phone_input">
                                <span>+91</span>
                                <input
                                    id="recharge-mobile"
                                    inputMode="numeric"
                                    maxLength="10"
                                    value={mobileNumber}
                                    onChange={(event) => {
                                        const nextNumber =
                                            event.target.value.replace(/\D/g, "");

                                        setMobileNumber(nextNumber);

                                        // A changed number invalidates the previous
                                        // operator, circle and amount selection.
                                        setSelectedOperator(null);
                                        setSelectedCircle(null);
                                        setAmount("");
                                    }}
                                    placeholder="Enter 10-digit mobile number"
                                />
                            </div>
                        </div>

                        {mobileNumber.length === 10 && (
                            <div className="mobile_recharge_step mobile_recharge_step_reveal">
                                {/* <div className="mobile_recharge_step_badge">2</div> */}
                                <div className="mobile_recharge_step_content">
                                    <div className="mobile_recharge_field">
                                        <div className="mobile_recharge_label_row">
                                            <label className="mobile_recharge_label">
                                                Select operator
                                            </label>

                                            <span className="mobile_recharge_selected_text">
                                                {selectedOperator?.operator_name || "Choose one"}
                                            </span>
                                        </div>

                                        <div className="mobile_recharge_operator_grid">
                                            {loading ? (
                                                [1, 2, 3, 4].map((item) => (
                                                    <div
                                                        className="mobile_recharge_operator_skeleton"
                                                        key={item}
                                                    />
                                                ))
                                            ) : operators.length === 0 ? (
                                                <div className="mobile_recharge_no_operator">
                                                    No operators found.
                                                </div>
                                            ) : (
                                                operators.map((operator) => (
                                                    <button
                                                        className={`mobile_recharge_operator_card ${selectedOperator?.operator_code === operator.operator_code
                                                            ? "selected"
                                                            : ""
                                                            }`}
                                                        key={operator.id || operator.operator_id}
                                                        type="button"
                                                        onClick={() => handleOperatorSelect(operator)}
                                                    >
                                                        <span className="mobile_recharge_operator_logo">
                                                            {operator.operator_image ? (
                                                                <img
                                                                    src={operator.operator_image}
                                                                    alt={operator.operator_name}
                                                                    className="mobile_recharge_operator_image"
                                                                />
                                                            ) : (
                                                                getOperatorInitial(operator.operator_name)
                                                            )}
                                                        </span>

                                                        <span>{operator.operator_name}</span>

                                                        {selectedOperator?.operator_code === operator.operator_code && (
                                                            <FiCheckCircle className="mobile_recharge_operator_check" />
                                                        )}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedOperator && (
                            <div className="mobile_recharge_step mobile_recharge_step_reveal">
                                {/* <div className="mobile_recharge_step_badge">3</div> */}
                                <div className="mobile_recharge_step_content">
                                    <div className="mobile_recharge_field">
                                        <label
                                            className="mobile_recharge_label"
                                            htmlFor="recharge-circle"
                                        >
                                            Select circle
                                        </label>

                                        <div className="mobile_recharge_select_wrap">
                                            <select
                                                id="recharge-circle"
                                                className="mobile_recharge_select"
                                                value={
                                                    selectedCircle?.circle_code || ""
                                                }
                                                onChange={(event) =>
                                                    setSelectedCircle(
                                                        circles.find(
                                                            (circle) =>
                                                                String(
                                                                    circle.circle_code
                                                                ) === event.target.value
                                                        ) || null
                                                    )
                                                }
                                                disabled={!selectedOperator}
                                            >
                                                <option value="">
                                                    Select your circle
                                                </option>

                                                {circles.map((circle) => (
                                                    <option
                                                        key={
                                                            circle.id ||
                                                            circle.circle_code
                                                        }
                                                        value={circle.circle_code}
                                                    >
                                                        {circle.circle_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <FiChevronDown />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedOperator && selectedCircle && (
                            <div className="mobile_recharge_step mobile_recharge_step_reveal">
                                <div className="mobile_recharge_step_content">
                                    <div className="mobile_recharge_field">

                                        <div className="mobile_recharge_label_row">
                                            <label className="mobile_recharge_label">
                                                Select plan
                                            </label>

                                            {selectedOperatorPlans.length > 0 && (
                                                <span className="mobile_recharge_amount_limit">
                                                    {selectedOperatorPlans.length} plans available
                                                </span>
                                            )}
                                            {selectedOperatorPlans.length === 0 && (
                                                <span>
                                                    No plans available
                                                </span>
                                            )}
                                        </div>

                                        {selectedOperatorPlans.length > 0 && (
                                            <div className="mobile_recharge_plan_grid">
                                                {selectedOperatorPlans.map((plan, index) => (
                                                    <button
                                                        type="button"
                                                        key={`${plan.operator_id}-${plan.amount}-${plan.duration}-${index}`}
                                                        className={`mobile_recharge_plan_card ${Number(amount) === Number(plan.amount)
                                                            ? "selected"
                                                            : ""
                                                            }`}
                                                        onClick={() =>
                                                            setAmount(String(plan.amount))
                                                        }
                                                    >
                                                        <div className="mobile_recharge_plan_top">
                                                            <strong>
                                                                ₹{plan.amount}
                                                            </strong>

                                                            <span>
                                                                {plan.duration} Days
                                                            </span>
                                                        </div>

                                                        {plan.description && (
                                                            <p>
                                                                {plan.description}
                                                            </p>
                                                        )}

                                                        {plan.channels &&
                                                            Object.keys(plan.channels).length > 0 && (
                                                                <div className="mobile_recharge_plan_channels">
                                                                    {Object.values(plan.channels)
                                                                        .slice(0, 4)
                                                                        .map((channel) => (
                                                                            <span key={channel}>
                                                                                {channel}
                                                                            </span>
                                                                        ))}
                                                                </div>
                                                            )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedOperator && selectedCircle && amount && (
                            <div className="mobile_recharge_summary">
                                <div>
                                    <span>Recharge to</span>
                                    <strong>
                                        +91 {mobileNumber || "----------"}
                                    </strong>
                                </div>

                                <div>
                                    <span>Operator</span>
                                    <strong>
                                        {selectedOperator.operator_name}
                                    </strong>
                                </div>

                                <div>
                                    <span>Amount</span>
                                    <strong>₹{amount || "0"}</strong>
                                </div>
                            </div>
                        )}

                        {error && (
                            <p className="mobile_recharge_error">
                                <FiXCircle />
                                {error}
                            </p>
                        )}

                        {paymentResponse?.status === true && (
                            <div className="pay_bills_success_box">
                                <FiCheckCircle />
                                <div>
                                    <strong>{paymentResponse.message}</strong>
                                    {paymentResponse.request_id && (
                                        <span>Request ID: {paymentResponse.request_id}</span>
                                    )}
                                    {paymentResponse?.transaction?.status && (
                                        <span>Status: {paymentResponse.transaction.status}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        <button
                            className="mobile_recharge_submit"
                            type="button"
                            disabled={
                                loading ||
                                submitting ||
                                !mobileNumber ||
                                !selectedOperator ||
                                !selectedCircle ||
                                !amount
                            }
                            onClick={handleRecharge}
                        >
                            {submitting ? (
                                <>
                                    <span className="mobile_recharge_spinner" />
                                    Processing recharge...
                                </>
                            ) : (
                                <>
                                    Recharge Now
                                    <FiArrowRight />
                                </>
                            )}
                        </button>

                        <p className="mobile_recharge_disclaimer">
                            By continuing, you confirm the mobile number and
                            recharge details are correct.
                        </p>
                    </div>
                </section>
            </div>
        </ThemedPageFrame>
    );
};

export default MobileRecharge;
